import { queryOptions } from '@tanstack/react-query';
import { createServerFn } from '@tanstack/react-start';
import { DEFAULT_TUTOR_LANGUAGE } from 'src/types/tutorLanguage';
import z from 'zod';

import { type Identity, resolveIdentity } from '../auth/resolveIdentity';
import { processKnowledgeProfile } from '../chatflow/modules/knowledgeProfile/knowledgeProfile';
import { getConversationForCaller, updateConversationData } from '../service/conversationService';

const getKnowledgeProfileSchema = z.object({
  conversationId: z.string(),
});

const fetchConversation = (conversationId: string, identity: Identity) =>
  getConversationForCaller(conversationId, identity);

const refreshStaleKnowledgeProfile = async (
  conversationId: string,
  sessionId: string,
  conversation: NonNullable<Awaited<ReturnType<typeof fetchConversation>>>,
) => {
  await updateConversationData(conversationId, { knowledgeUpdating: true });

  const identity = await resolveIdentity();
  if (!identity) {
    throw new Error('No active session');
  }

  try {
    const updatedKnowledgeProfile = await processKnowledgeProfile({
      businessUnit: conversation.businessUnit ?? '',
      conversationId,
      sessionId,
      eventId: conversation.eventId,
      moduleObservations: conversation.moduleObservations,
      identity,
      language: conversation.language ?? DEFAULT_TUTOR_LANGUAGE,
    });

    return {
      updated: Boolean(updatedKnowledgeProfile),
      knowledgeProfile: updatedKnowledgeProfile,
    };
  } catch (error) {
    throw new Error('Failed to update knowledge profile', { cause: error });
  } finally {
    // In case of failure, ensure we reset the updating flag.
    // Knowledge profile update errors are handled in orchestration,
    // which retries when knowledgeProfileStale=true && knowledgeUpdating=false.
    await updateConversationData(conversationId, { knowledgeUpdating: false });
  }
};

const getKnowledgeProfile = async (conversationId: string) => {
  const identity = await resolveIdentity();
  if (!identity) {
    throw new Error('No active session');
  }
  const conversation = await fetchConversation(conversationId, identity);

  if (conversation?.knowledgeProfileStale) {
    return refreshStaleKnowledgeProfile(conversationId, identity.sessionId, conversation);
  }

  return {
    updated: false,
    knowledgeProfile: conversation?.knowledgeProfile,
  };
};

export const getKnowledgeProfileFn = createServerFn({ method: 'GET' })
  .validator(getKnowledgeProfileSchema)
  .handler(({ data }) => getKnowledgeProfile(data.conversationId));

export const knowledgeProfileQueryOptions = (conversationId: string) =>
  queryOptions({
    queryKey: ['knowledgeProfile', conversationId],
    queryFn: () => getKnowledgeProfileFn({ data: { conversationId } }),
    retry: 3,
  });
